"use client";

import {
  ArrowRight,
  Code,
  Cpu,
  Database,
  FileJson,
  GitBranch,
  Layers,
  Play,
  Terminal,
  Webhook,
} from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { FadeInUp, SlideInLeft, SlideInRight } from "@/components/ui/motion";

const capabilities = [
  { icon: Terminal, label: "REST API", description: "Full CRUD operations" },
  { icon: Webhook, label: "Webhooks", description: "Real-time events" },
  { icon: GitBranch, label: "Environments", description: "Dev, staging, prod" },
  { icon: Play, label: "Replay & Debug", description: "Test any execution" },
  {
    icon: Database,
    label: "Export & Backup",
    description: "Your data, your way",
  },
  { icon: Cpu, label: "Custom Code", description: "JavaScript & Python" },
];

const codeExamples = {
  webhook: `// Incoming webhook payload
{
  "event": "order.created",
  "data": {
    "order_id": "ord_8x7k2n",
    "customer": "cust_4m9p1r",
    "total": 149.99,
    "items": [...]
  },
  "timestamp": "2024-01-15T10:32:18Z"
}`,
  api: `// Trigger a workflow via API
const response = await fetch(
  'https://api.makeplan.dev/v1/workflows/wf_abc123/trigger',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer mp_sk_...',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      customer_id: 'cust_4m9p1r',
      action: 'send_welcome_email'
    })
  }
);

const { execution_id } = await response.json();`,
};

export default function DevelopersPage() {
  const navigate = useRouter();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-20 pointer-events-none" />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-warning/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeInUp className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-warning/10 border border-warning/30 text-warning text-sm font-medium mb-6">
              <Terminal className="w-4 h-4" />
              For developers
            </div>
            <h1 className="text-foreground mb-6">
              Built for teams that need
              <br />
              <span className="gradient-text">control</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Full API access, webhook flexibility, and the observability tools
              you expect. Build complex integrations while keeping full
              visibility into what's running.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="gradient"
                size="lg"
                onClick={() => navigate.push("/register")}
              >
                Start building
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" size="lg">
                <FileJson className="w-4 h-4 mr-2" />
                API docs (coming soon)
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="text-center mb-14">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Developer capabilities
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to integrate and extend
            </p>
          </FadeInUp>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="flex items-center gap-4 p-5 rounded-xl card-interactive"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <cap.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-semibold text-foreground">
                    {cap.label}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {cap.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Content */}
            <SlideInLeft>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-5">
                Simple, powerful <span className="gradient-text">API</span>
              </h2>

              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Trigger workflows, manage executions, and access your data
                programmatically. RESTful design with comprehensive
                documentation.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Layers className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">
                      RESTful endpoints
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Standard HTTP methods and JSON responses
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Code className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">
                      SDKs coming soon
                    </p>
                    <p className="text-sm text-muted-foreground">
                      JavaScript, Python, and more
                    </p>
                  </div>
                </div>
              </div>
            </SlideInLeft>

            {/* Code Block */}
            <SlideInRight>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-warning/20 rounded-2xl blur-2xl opacity-50" />

                <motion.div
                  className="relative glass rounded-xl shadow-2xl overflow-hidden"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-card/50">
                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                    <div className="w-3 h-3 rounded-full bg-warning/60" />
                    <div className="w-3 h-3 rounded-full bg-success/60" />
                    <span className="ml-3 text-xs font-mono text-muted-foreground">
                      trigger-workflow.js
                    </span>
                  </div>
                  <pre className="p-5 overflow-x-auto">
                    <code className="text-sm font-mono text-foreground leading-relaxed">
                      {codeExamples.api}
                    </code>
                  </pre>
                </motion.div>
              </div>
            </SlideInRight>
          </div>
        </div>
      </section>

      {/* Webhook Example */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Code Block */}
            <SlideInLeft>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-warning/20 to-accent/20 rounded-2xl blur-2xl opacity-50" />

                <motion.div
                  className="relative glass rounded-xl shadow-2xl overflow-hidden"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-card/50">
                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                    <div className="w-3 h-3 rounded-full bg-warning/60" />
                    <div className="w-3 h-3 rounded-full bg-success/60" />
                    <span className="ml-3 text-xs font-mono text-muted-foreground">
                      webhook-payload.json
                    </span>
                  </div>
                  <pre className="p-5 overflow-x-auto">
                    <code className="text-sm font-mono text-foreground leading-relaxed">
                      {codeExamples.webhook}
                    </code>
                  </pre>
                </motion.div>
              </div>
            </SlideInLeft>

            {/* Content */}
            <SlideInRight>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-5">
                Webhooks that just <span className="gradient-text">work</span>
              </h2>

              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Receive events from any service. Each workflow gets a unique
                endpoint. Automatic retries and detailed logging for every
                request.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Webhook className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">
                      Unique endpoints
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Each workflow has its own webhook URL
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Play className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">
                      Replay any request
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Debug with the exact payload that was received
                    </p>
                  </div>
                </div>
              </div>
            </SlideInRight>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Ready to build?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start integrating in minutes. Full API access on all plans.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="gradient"
                size="lg"
                onClick={() => navigate.push("/register")}
              >
                Start building free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate.push("/contact")}
              >
                Talk to us
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
}
